/**
 * Patient Match Panel Component
 *
 * Right panel for patient search, results display, and action buttons.
 * Matches desktop patient_match_panel.py functionality.
 *
 * Features:
 * - Search button
 * - Display matched patients with radio selection
 * - Update/Create buttons
 * - Switch to comparison view
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Search, PersonAdd, Visibility } from '@mui/icons-material';
import { useSessionStore } from '../../store/sessionStore';
import { usePDFStore } from '../../store/pdfStore';
import { patientsApi } from '../../api/patients';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { PatientComparisonView } from './PatientComparisonView';
import type { PatientMatchResult } from '../../types/api';

interface PatientMatchPanelProps {
  onAfterSave?: () => void;
}

export const PatientMatchPanel: React.FC<PatientMatchPanelProps> = ({ onAfterSave }) => {
  const { customerId, practiceId, emrConnectionId, activeSession } = useSessionStore();
  const { tebraWorkflowActive, currentPDF } = usePDFStore();
  const pdf = currentPDF;
  const { showSuccess, showError: showSnackbarError } = useSnackbar();
  const { validateAllFields } = useFieldValidation();

  const [matchedPatients, setMatchedPatients] = useState<PatientMatchResult[]>([]);
  const [selectedPatientIndex, setSelectedPatientIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showComparisonView, setShowComparisonView] = useState(false);
  const [fullPatientData, setFullPatientData] = useState<Record<string, any> | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Reset right panel when navigating to a new PDF (mirrors desktop _clear_right_panel → clear())
  useEffect(() => {
    setShowComparisonView(false);
    setFullPatientData(null);
    setSelectedPatientIndex(null);
    setMatchedPatients([]);
    setError('');
    setHasSearched(false);
  }, [pdf?.pdf_id]);

  // Automated EMR search when navigation occurs (if Tebra workflow is active)
  useEffect(() => {
    if (tebraWorkflowActive && pdf && pdf.LastName) {
      handleSearch();
    }
  }, [pdf?.pdf_id, tebraWorkflowActive]);

  const handleSearch = async () => {
    setError('');
    setLoading(true);

    try {
      // Build search request from parsed widget fields — matches desktop main_window.py line 361
      const searchRequest = {
        last_name: pdf?.LastName || '',
        first_name: pdf?.FirstName || '',
        middle_name: pdf?.MiddleName || '',
        date_of_birth: pdf?.DateOfBirth || undefined,
        medical_record_number: pdf?.MedicalRecordNumber || undefined,
      };

      const results = await patientsApi.searchPatients(
        searchRequest,
        customerId,
        practiceId,
        emrConnectionId,
        activeSession?.session_id || ''
      );

      setMatchedPatients(results.matches);
      setHasSearched(true);

      if (results.total_matches === 0) {
        // No error set — UI shows "no results" state with Create New button
      } else if (results.matches.length === 1 && results.matches[0].match_type === 'exact') {
        // 100% match — auto-fetch full details and show comparison (matches desktop behavior)
        setSelectedPatientIndex(0);
        await fetchFullPatientDetails(results.matches[0]);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : typeof err?.message === 'string' ? err.message : String(err);
      setError('Failed to search patients: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchFullPatientDetails = async (match: PatientMatchResult) => {
    // Matches desktop gui/main_window.py:481 — fetch full patient record (demographics + guarantor + insurance)
    try {
      const patientId = match.patient_data.PatientID;
      const details = await patientsApi.getPatientDetails(
        patientId,
        customerId,
        practiceId,
        emrConnectionId,
        activeSession?.session_id || ''
      );
      setFullPatientData(details);
      setShowComparisonView(true);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : typeof err?.message === 'string' ? err.message : String(err);
      setError('Failed to fetch patient details: ' + msg);
    }
  };

  const handleCreateNew = async () => {
    setError('');

    // Validate all required fields first
    const validation = validateAllFields(currentPDF);
    if (!validation.isValid) {
      const errorMessage = 'Validation failed:\n' + validation.errors.join('\n');
      setError(errorMessage);
      showSnackbarError('Please fix validation errors before creating patient');
      return;
    }

    setLoading(true);

    try {
      // Build patient data from parsed widget fields
      // Gender: desktop stores "Male"/"Female", Tebra API expects "M"/"F"/"U"
      const genderRaw = pdf?.Gender || '';
      let genderCode: 'M' | 'F' | 'U' | undefined;
      if (genderRaw.toUpperCase().startsWith('F')) genderCode = 'F';
      else if (genderRaw.toUpperCase().startsWith('M')) genderCode = 'M';
      else if (genderRaw) genderCode = 'U';

      // Guarantor (if not same as patient) — matches desktop get_guarantor_data()
      let guarantor: any = undefined;
      if (!pdf?.SameAsPatient && pdf?.GuarantorName) {
        const nameParts = pdf.GuarantorName.trim().split(/\s+/);
        guarantor = {
          FirstName: nameParts[0] || undefined,
          LastName: nameParts.slice(1).join(' ') || undefined,
          RelationshiptoGuarantor: pdf.GuarantorRelationship || undefined,
        };
      }

      // Primary insurance — matches desktop get_primary_insurance()
      let primary_insurance: any = undefined;
      if (pdf?.PrimaryInsuranceCompany) {
        primary_insurance = {
          CompanyName: pdf.PrimaryInsuranceCompany,
          PlanName: pdf.PrimaryPlanName || undefined,
          PolicyNumber: pdf.PrimaryPolicyNumber || undefined,
          PolicyGroupNumber: pdf.PrimaryGroupNumber || undefined,
          Insured: pdf.PrimarySubscriberName || undefined,
          InsuredDateOfBirth: pdf.PrimarySubscriberDOB || undefined,
          InsuredGender: pdf.PrimarySubscriberGender || undefined,
          RelationshipToInsured: pdf.PrimarySubscriberRelationship || undefined,
          InsuranceType: pdf.PrimaryInsuranceType || undefined,
        };
      }

      // Secondary insurance — matches desktop get_secondary_insurance()
      let secondary_insurance: any = undefined;
      if (pdf?.SecondaryInsuranceCompany) {
        secondary_insurance = {
          CompanyName: pdf.SecondaryInsuranceCompany,
          PlanName: pdf.SecondaryPlanName || undefined,
          PolicyNumber: pdf.SecondaryPolicyNumber || undefined,
          PolicyGroupNumber: pdf.SecondaryGroupNumber || undefined,
          Insured: pdf.SecondarySubscriberName || undefined,
          InsuredDateOfBirth: pdf.SecondarySubscriberDOB || undefined,
          InsuredGender: pdf.SecondarySubscriberGender || undefined,
          RelationshipToInsured: pdf.SecondarySubscriberRelationship || undefined,
          InsuranceType: pdf.SecondaryInsuranceType || undefined,
        };
      }

      const patientData = {
        patient: {
          FirstName: pdf?.FirstName || '',
          LastName: pdf?.LastName || '',
          MiddleName: pdf?.MiddleName || undefined,
          DateOfBirth: pdf?.DateOfBirth || undefined,
          Gender: genderCode,
          MedicalRecordNumber: pdf?.MedicalRecordNumber || undefined,
          AddressLine1: pdf?.AddressLine1 || undefined,
          City: pdf?.City || undefined,
          State: pdf?.State || undefined,
          ZipCode: pdf?.ZipCode || undefined,
          MobilePhone: pdf?.MobilePhone || undefined,
          EmailAddress: pdf?.EmailAddress || undefined,
        },
        ...(guarantor && { guarantor }),
        ...(primary_insurance && { primary_insurance }),
        ...(secondary_insurance && { secondary_insurance }),
      };

      await patientsApi.createPatient(
        patientData,
        customerId,
        practiceId,
        emrConnectionId,
        activeSession?.session_id
      );

      showSuccess('Patient created successfully in Tebra EMR!');
      setError('');
      onAfterSave?.();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : typeof err?.message === 'string' ? err.message : String(err);
      const errorMsg = 'Failed to create patient: ' + msg;
      setError(errorMsg);
      showSnackbarError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePatient = async () => {
    if (selectedPatientIndex === null) {
      setError('Please select a patient first');
      return;
    }

    // Fetch full patient details from Tebra before showing comparison (matches desktop flow)
    setLoading(true);
    setError('');
    try {
      await fetchFullPatientDetails(matchedPatients[selectedPatientIndex]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFromComparison = async (updateData: any) => {
    if (selectedPatientIndex === null || !fullPatientData) return;

    setError('');
    setLoading(true);

    try {
      const patientId = fullPatientData.PatientID;

      await patientsApi.updatePatient(
        patientId,
        updateData,
        customerId,
        practiceId,
        emrConnectionId,
        activeSession?.session_id
      );

      showSuccess('Patient record updated successfully in Tebra EMR!');
      setShowComparisonView(false);
      onAfterSave?.();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : typeof err?.message === 'string' ? err.message : String(err);
      setError('Failed to update patient: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromComparison = () => {
    setShowComparisonView(false);
    setFullPatientData(null);
  };

  // Show comparison view if full patient data has been fetched
  if (showComparisonView && selectedPatientIndex !== null && fullPatientData) {
    return (
      <PatientComparisonView
        fullPatientData={fullPatientData}
        onBack={handleBackFromComparison}
        onSave={handleSaveFromComparison}
        onCreateNew={handleCreateNew}
      />
    );
  }

  // Otherwise show search view
  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        Patient Matching
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Button */}
      <Button
        variant="contained"
        fullWidth
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
        onClick={handleSearch}
        disabled={loading}
        sx={{ mb: 2 }}
      >
        {loading ? 'Searching...' : 'Search Tebra EMR'}
      </Button>

      {/* Search Results */}
      {matchedPatients.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Found {matchedPatients.length} match{matchedPatients.length !== 1 ? 'es' : ''}
          </Typography>

          <Paper variant="outlined" sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
            <RadioGroup
              value={selectedPatientIndex}
              onChange={(e) => setSelectedPatientIndex(parseInt(e.target.value))}
            >
              <List disablePadding>
                {matchedPatients.map((match, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderBottom: index < matchedPatients.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                    }}
                  >
                    <FormControlLabel
                      value={index}
                      control={<Radio />}
                      sx={{ width: '100%', m: 0 }}
                      label={
                        <Box sx={{ ml: 1, width: '100%' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {match.patient_data.LastName}, {match.patient_data.FirstName}{' '}
                            {match.patient_data.MiddleName || ''}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {[
                              match.patient_data.MedicalRecordNumber && `MRN: ${match.patient_data.MedicalRecordNumber}`,
                              match.patient_data.Gender && `Gender: ${match.patient_data.Gender}`,
                              (match.patient_data.City || match.patient_data.State) &&
                                [match.patient_data.City, match.patient_data.State].filter(Boolean).join(', '),
                              match.patient_data.MobilePhone && `Ph: ${match.patient_data.MobilePhone}`,
                              match.patient_data.LastVisitDate && `Last Visit: ${match.patient_data.LastVisitDate}`,
                            ].filter(Boolean).join(' | ')}
                          </Typography>

                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={`${Math.round(match.confidence * 100)}% match`}
                              size="small"
                              color={match.match_type === 'exact' ? 'success' : 'warning'}
                            />
                            {match.match_reasons.map((reason, i) => (
                              <Chip
                                key={i}
                                label={reason}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 0.5 }}
                              />
                            ))}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </RadioGroup>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Visibility />}
              onClick={handleUpdatePatient}
              disabled={selectedPatientIndex === null || loading}
            >
              View & Compare
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<PersonAdd />}
              onClick={handleCreateNew}
              disabled={loading}
            >
              Create New
            </Button>
          </Box>
        </>
      )}

      {/* No results state */}
      {!loading && matchedPatients.length === 0 && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            gap: 2,
          }}
        >
          {hasSearched ? (
            <>
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                No matching patients found in Tebra EMR.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAdd />}
                onClick={handleCreateNew}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create New Patient in Tebra'}
              </Button>
            </>
          ) : (
            <Typography variant="body2">
              Click "Search Tebra EMR" to find matching patients
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
